/**
 * @file     ListingSection.tsx
 * @purpose  One listing-detail section: a hairline top, an optional eyebrow,
 *           an H2 at 24/600 and at most one trailing control (a
 *           SegmentedControl or a link) on the heading row, then the body.
 *           Its id is a SectionAnchors target, and its scroll margin clears
 *           the sticky bar. Replaces PanelPrimitives and the tab panels.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { eyebrowSx, hairline, hubColors } from '@/components/hub/hubKitStyles';

export interface ListingSectionProps {
  /** Anchor id for SectionAnchors. */
  id?: string;
  title: ReactNode;
  eyebrow?: string;
  /** One control on the heading row. */
  action?: ReactNode;
  children: ReactNode;
  /** Drop the top hairline, e.g. for the first section under the hero. */
  flush?: boolean;
  /** Short label for the section anchors (e.g. "Costs"). Defaults to none, so the section stays off the rail. */
  anchorLabel?: string;
  /** 'h3' for a quick view, where the listing heading is the h2: a 19px heading and the tighter rhythm. */
  headingLevel?: 'h2' | 'h3';
}

export function ListingSection({ id, title, eyebrow, action, children, flush = false, anchorLabel, headingLevel = 'h2' }: ListingSectionProps) {
  const headingId = id ? `${id}-heading` : undefined;
  const compact = headingLevel === 'h3';
  const gap = compact ? 6 : { xs: 6, md: 8 };
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={headingId}
      data-listing-section
      data-anchor-label={anchorLabel}
      sx={{
        pt: flush ? 0 : gap,
        mt: flush ? 0 : gap,
        borderTop: flush ? 'none' : hairline,
        // Clears the header (scroll-padding on the scroll root) plus the sticky anchors bar.
        scrollMarginTop: 72,
        '&:focus': { outline: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: compact ? 'center' : 'flex-end', justifyContent: 'space-between', gap: 4, mb: compact ? 4 : 5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          {eyebrow ? (
            <Box component="p" sx={eyebrowSx}>
              {eyebrow}
            </Box>
          ) : null}
          <Box
            component={headingLevel}
            id={headingId}
            sx={{ m: 0, fontSize: compact ? 19 : { xs: 22, md: 24 }, fontWeight: fontWeightTokens.semibold, lineHeight: 1.25, letterSpacing: '-0.012em', color: hubColors.textPrimary }}
          >
            {title}
          </Box>
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
      {children}
    </Box>
  );
}
